import { toUtf8 } from "@aws-sdk/util-utf8-browser"
import { iot, mqtt5 } from "aws-iot-device-sdk-v2/dist/browser"

import { useAppDispatch } from "../app/hooks/hooks"
import {
  addSubscribedTopic,
  setConnectionStatus,
  setError,
  setLastMessage,
  setSubscribedTopics,
  setUserStatus,
  showNotificationToast,
} from "../app/slices/wsSlice"
import { debugLog } from "./debugUtils"

class WebSocketService {
  private dispatch: ReturnType<typeof useAppDispatch> | null = null
  private api: any = null
  private client: mqtt5.Mqtt5Client | null = null
  private reconnectTimer: NodeJS.Timeout | null = null
  private isConnecting = false
  private retryCount = 0
  private MAX_RETRIES = 5
  private topicCallbacks = new Map<
    string,
    Set<(topic: string, message: any) => void>
  >()
  private subscribedTopics = new Set<string>()
  private pendingSubscriptions = new Map<
    string,
    Set<(topic: string, message: any) => void>
  >()
  private refetchConfig: (() => Promise<any>) | null = null

  setDispatch(dispatch: ReturnType<typeof useAppDispatch>) {
    this.dispatch = dispatch
  }

  setApi(api: any) {
    this.api = api
  }

  setRefetchConfig(refetchConfig: () => Promise<any>) {
    this.refetchConfig = refetchConfig
  }

  async connect(wsConfig: any) {
    if (this.isConnecting || this.client) {
      return
    }

    if (this.retryCount > this.MAX_RETRIES) {
      debugLog("Max retries exceeded. Giving up.")
      this.dispatch?.(setError("Max retries exceeded. Giving up."))
      return
    }

    this.isConnecting = true
    this.dispatch?.(setConnectionStatus("connecting"))

    try {
      // Create custom auth config for MQTT5
      const customAuthConfig: iot.MqttConnectCustomAuthConfig = {
        authorizerName: wsConfig.authorizerName,
        tokenKeyName: wsConfig.tokenKeyName,
        tokenValue: wsConfig.token,
        tokenSignature: wsConfig.tokenSignature,
      }

      // Create MQTT5 client config
      const builder =
        iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithCustomAuth(
          wsConfig.iotEndpoint,
          customAuthConfig,
        )

      this.client = new mqtt5.Mqtt5Client(builder.build())

      // Set up event listeners
      this.client.on("messageReceived", (eventData) =>
        this.onMessageReceived(eventData),
      )

      this.client.on("connectionSuccess", (eventData) => {
        debugLog("connectionSuccess", eventData)
        this.isConnecting = false
        this.retryCount = 0
        this.dispatch?.(setConnectionStatus("connected"))
        // Subscribe to delivery-specific topics
        const mblsProviderId = wsConfig.mblsProviderId
        const topics = [
          `provider/order/${mblsProviderId}`,
          `provider/notification/${mblsProviderId}`,
        ]
        this.subscribeToTopics(topics)
        this.processPendingSubscriptions()
      })

      this.client.on("connectionFailure", (error) => {
        debugLog("connectionFailure", error)
        this.client = null
        this.isConnecting = false
        this.retryCount++
        this.dispatch?.(setError("Connection failed"))
        this.scheduleReconnect(wsConfig)
      })

      this.client.on("disconnection", (eventData) => {
        debugLog("Disconnected from AWS IoT", eventData)
        this.client = null
        this.dispatch?.(setConnectionStatus("disconnected"))
        this.scheduleReconnect(wsConfig)
      })

      // Start the connection
      this.client.start()
    } catch (error) {
      console.error("Failed to connect to AWS IoT:", error)
      this.isConnecting = false
      this.retryCount++
      this.dispatch?.(
        setError(error instanceof Error ? error.message : "Connection failed"),
      )
      this.scheduleReconnect(wsConfig)
    }
  }

  private onMessageReceived(eventData: any) {
    debugLog("Message received", eventData)
    const { message } = eventData
    const { topicName, payload } = message

    if (!payload) {
      debugLog("payload is null")
      return
    }

    // Convert payload to string
    const messageStr = toUtf8(payload as Uint8Array)

    // Attempt to parse the message
    try {
      const data = JSON.parse(messageStr)
      this.handleMessage(topicName, data)
    } catch (err) {
      debugLog("Error parsing message", err)
    }
  }

  private async subscribeToTopics(topics: string[]) {
    if (!this.client) return

    try {
      const subscriptions = topics.map((topic) => ({
        topicFilter: topic,
        qos: mqtt5.QoS.AtLeastOnce,
      }))

      const subResp = await this.client.subscribe({
        subscriptions,
      })

      for (let i = 0; i < subscriptions.length; i++) {
        const reasonCode = subResp?.reasonCodes[i]
        const sub = subscriptions[i]
        if (["0", "1", "2"].includes(reasonCode + "")) {
          debugLog(`Successfully subscribed to: [${sub.topicFilter}]`)
          this.dispatch?.(addSubscribedTopic(sub.topicFilter))
        } else {
          console.error(`Error subscribing : [${sub.topicFilter}]`, reasonCode)
        }
      }

      this.dispatch?.(setSubscribedTopics(topics))
    } catch (error) {
      console.error("Failed to subscribe to topics:", error)
      this.dispatch?.(setError("Failed to subscribe to topics"))
    }
  }

  private async processPendingSubscriptions() {
    if (this.pendingSubscriptions.size === 0) return

    debugLog(
      "Processing pending subscriptions:",
      Array.from(this.pendingSubscriptions.keys()),
    )

    for (const [topic, callbacks] of this.pendingSubscriptions) {
      // Move to active callbacks
      let activeCallbacks = this.topicCallbacks.get(topic)
      if (!activeCallbacks) {
        activeCallbacks = new Set()
        this.topicCallbacks.set(topic, activeCallbacks)
      }
      callbacks.forEach((callback) => activeCallbacks!.add(callback))

      // Subscribe if not already
      if (!this.subscribedTopics.has(topic)) {
        try {
          const subResp = await this.client!.subscribe({
            subscriptions: [
              {
                topicFilter: topic,
                qos: mqtt5.QoS.AtLeastOnce,
              },
            ],
          })
          const reasonCode = subResp?.reasonCodes[0]
          if (["0", "1", "2"].includes(reasonCode + "")) {
            debugLog(`Successfully subscribed to pending topic: [${topic}]`)
            this.dispatch?.(addSubscribedTopic(topic))
            this.subscribedTopics.add(topic)
          } else {
            console.error(
              `Error subscribing to pending topic: [${topic}]`,
              reasonCode,
            )
          }
        } catch (error) {
          console.error("Failed to subscribe to pending topic:", topic, error)
        }
      }
    }

    // Clear pending
    this.pendingSubscriptions.clear()
  }

  async subscribe(
    topics: string[],
    onMessage?: (topic: string, message: any) => void,
  ) {
    if (this.getConnectionStatus() !== "connected" || !this.client) {
      debugLog(
        "WebSocket client not connected. Buffering subscription for topics:",
        topics,
      )
      for (const topic of topics) {
        let callbacks = this.pendingSubscriptions.get(topic)
        if (!callbacks) {
          callbacks = new Set()
          this.pendingSubscriptions.set(topic, callbacks)
        }
        if (onMessage) {
          callbacks.add(onMessage)
        }
      }
      return
    }
    debugLog("Subscribing to topics:", topics)
    try {
      const topicsToSubscribe: string[] = []

      for (const topic of topics) {
        let callbacks = this.topicCallbacks.get(topic)
        if (!callbacks) {
          callbacks = new Set()
          this.topicCallbacks.set(topic, callbacks)
        }

        if (onMessage) {
          callbacks.add(onMessage)
        }

        if (!this.subscribedTopics.has(topic)) {
          topicsToSubscribe.push(topic)
          this.subscribedTopics.add(topic)
        }
      }

      if (topicsToSubscribe.length > 0) {
        const subscriptions = topicsToSubscribe.map((topic) => ({
          topicFilter: topic,
          qos: mqtt5.QoS.AtLeastOnce,
        }))

        const subResp = await this.client.subscribe({
          subscriptions,
        })

        for (let i = 0; i < subscriptions.length; i++) {
          const reasonCode = subResp?.reasonCodes[i]
          const sub = subscriptions[i]
          if (["0", "1", "2"].includes(reasonCode + "")) {
            debugLog(`Successfully subscribed to: [${sub.topicFilter}]`)
            this.dispatch?.(addSubscribedTopic(sub.topicFilter))
          } else {
            console.error(
              `Error subscribing : [${sub.topicFilter}]`,
              reasonCode,
            )
            this.subscribedTopics.delete(sub.topicFilter)
          }
        }
      }
    } catch (error) {
      console.error("Failed to subscribe:", error)
    }
  }

  async unsubscribe(
    topics: string[],
    onMessage?: (topic: string, message: any) => void,
  ) {
    if (this.getConnectionStatus() !== "connected" || !this.client) {
      console.warn(
        "WebSocket client not connected. Cannot unsubscribe from topics.",
      )
      return
    }

    try {
      const topicsToUnsubscribe: string[] = []

      for (const topic of topics) {
        const callbacks = this.topicCallbacks.get(topic)
        if (callbacks && onMessage) {
          callbacks.delete(onMessage)
          if (callbacks.size === 0) {
            this.topicCallbacks.delete(topic)
            if (this.subscribedTopics.has(topic)) {
              topicsToUnsubscribe.push(topic)
              this.subscribedTopics.delete(topic)
            }
          }
        }
      }

      if (topicsToUnsubscribe.length > 0) {
        await this.client.unsubscribe({
          topicFilters: topicsToUnsubscribe,
        })
        debugLog("Successfully unsubscribed from:", topicsToUnsubscribe)
      }
    } catch (error) {
      console.error("Failed to unsubscribe:", error)
    }
  }

  private handleMessage(topic: string, message: any) {
    try {
      debugLog("Received message:", { topic, message })

      this.dispatch?.(setLastMessage({ topic, message, timestamp: Date.now() }))

      // Check for topic-specific callbacks
      const topicCallbacks = this.topicCallbacks.get(topic)
      if (topicCallbacks) {
        topicCallbacks.forEach((callback) => {
          try {
            callback(topic, message)
          } catch (error) {
            console.error("Error in topic callback:", error)
          }
        })
      }

      // Handle user status updates
      if (topic.endsWith("/status") && message.lastActivityAt) {
        this.dispatch?.(setUserStatus({ lastActivity: message.lastActivityAt }))
      }

      // Handle entity updates for Delivery — invalidate RTK Query Orders tag
      this.handleEntityUpdate(topic, message)
    } catch (error) {
      console.error("Failed to parse message:", error)
    }
  }

  private handleEntityUpdate(topic: string, message: any) {
    if (!this.api || !this.dispatch) return

    // For delivery: any message on order or notification topics invalidates Orders
    if (
      topic.startsWith("provider/order/") ||
      topic.startsWith("provider/notification/")
    ) {
      debugLog("Invalidating Orders cache due to MQTT message on:", topic)
      this.dispatch(
        this.api.util.invalidateTags([
          { type: "Orders", id: "LIST" },
          { type: "Orders", id: "DASHBOARD" },
        ]),
      )
    }

    // Notification topic also invalidates Notifications tag for bell icon refresh
    if (topic.startsWith("provider/notification/")) {
      debugLog(
        "Invalidating Notifications cache due to MQTT message on:",
        topic,
      )
      this.dispatch(this.api.util.invalidateTags(["Notifications"]))

      // Show notification toast
      const notification = message?.data?.notification
      if (notification) {
        this.dispatch(
          showNotificationToast({
            title: notification.title ?? "",
            body: notification.body ?? "",
            orderId: notification.orderId ?? null,
          }),
        )
      }
    }
  }

  private async scheduleReconnect(wsConfig: any) {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    this.reconnectTimer = setTimeout(async () => {
      debugLog("Attempting to reconnect...")
      try {
        const freshConfig = this.refetchConfig
          ? await this.refetchConfig()
          : null
        this.connect(freshConfig ?? wsConfig)
      } catch (error) {
        debugLog("Failed to refetch config for reconnection, using cached config")
        this.connect(wsConfig)
      }
    }, 5000)
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.client) {
      this.client.stop()
      this.client = null
    }

    this.isConnecting = false
    this.retryCount = 0
    this.dispatch?.(setConnectionStatus("disconnected"))
  }

  isConnected(): boolean {
    return this.client !== null
  }

  getConnectionStatus(): "disconnected" | "connecting" | "connected" | "error" {
    if (this.isConnecting) return "connecting"
    if (this.client) return "connected"
    return "disconnected"
  }

  async publish(topicName: string, message: any) {
    if (this.getConnectionStatus() !== "connected" || !this.client) {
      console.error("WebSocket client not connected. Cannot publish.")
      return
    }

    try {
      const payload = JSON.stringify(message)
      const pubResp = await this.client.publish({
        topicName,
        payload: new TextEncoder().encode(payload),
        qos: mqtt5.QoS.AtLeastOnce,
      })

      if (pubResp?.reasonCode + "" !== "0") {
        console.error("Error publishing to: ", topicName, pubResp)
        return
      }

      debugLog("Successfully published to: ", topicName)
    } catch (error) {
      console.error("Error publishing", error)
    }
  }
}

// Export singleton instance
export const wsService = new WebSocketService()
